import { Group, Table, Text } from '@mantine/core';
import { PiCheckBold, PiXBold } from 'react-icons/pi';

export const fmtPrice = (
  amount: number | null | undefined,
  currency: string | null | undefined,
) =>
  amount != null
    ? `${Number(amount).toLocaleString()} ${currency || ''}`.trim()
    : null;

export type MatrixTierCell = {
  included: boolean;
  label?: string;
};

export type MatrixRow = {
  key: string;
  name: string;
  price: string | null;
  tiers?: MatrixTierCell[];
  dimmed?: boolean;
  /**
   * Whether the customer picked this line, in a block where each service is
   * chosen individually rather than by package.
   *
   * Three states, and the third matters: `true` marks it chosen, `false` marks it
   * passed over, and `undefined` means no choice has been made yet — an offer being
   * previewed before the customer has touched it must not render every line as
   * rejected.
   */
  selected?: boolean;
};

const CHOSEN_BG = 'var(--mantine-color-green-light)';

function TierCellContent({
  cell,
  dimmed,
}: {
  cell: MatrixTierCell;
  dimmed: boolean;
}) {
  if (cell.label != null) {
    return (
      <Text
        fz="xs"
        span
        c={dimmed ? 'dimmed' : undefined}
        style={{ whiteSpace: 'nowrap' }}
      >
        {cell.label}
      </Text>
    );
  }
  if (cell.included) {
    return (
      <PiCheckBold
        size={14}
        color={
          dimmed
            ? 'var(--mantine-color-gray-5)'
            : 'var(--mantine-color-green-7)'
        }
      />
    );
  }
  return <PiXBold size={14} color="var(--mantine-color-gray-5)" />;
}

export default function ServiceMatrixTable({
  title,
  tierNames,
  selectedTier,
  rows,
  totals,
}: {
  title: string;
  tierNames: string[];
  selectedTier?: number | null;
  rows: MatrixRow[];
  /**
   * What each package costs, already formatted, in tier order - or a single figure for a
   * block with no packages. Omitted when the block does not state a total.
   *
   * The customer's page and the proposal PDF both close a block with this row. Only the
   * offer page left it out, so the one view staff read was the one that never said what a
   * package costs - which is how a schedule came to be questioned that was correct.
   */
  totals?: (string | null)[] | null;
}) {
  if (rows.length === 0) return null;

  const isSingleTier = tierNames.length === 0;
  const hasChoice = !isSingleTier && selectedTier != null;
  const isChosen = (tier: number) => hasChoice && tier === selectedTier;
  const isDimmedTier = (tier: number) => hasChoice && tier !== selectedTier;

  return (
    <Table
      withRowBorders={false}
      verticalSpacing={2}
      horizontalSpacing={6}
      fz="xs"
      layout="auto"
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th p={0} px={6}>
            <Text fz="xs" fw={500} truncate>
              {title}
            </Text>
          </Table.Th>
          {tierNames.map((name, tier) => (
            <Table.Th
              key={tier}
              p={0}
              px={6}
              ta="center"
              style={{
                whiteSpace: 'nowrap',
                backgroundColor: isChosen(tier) ? CHOSEN_BG : undefined,
              }}
            >
              <Group gap={4} justify="center" wrap="nowrap">
                <Text
                  fz="xs"
                  fw={500}
                  c={isChosen(tier) ? 'green.8' : 'dimmed'}
                >
                  {name}
                </Text>
                {isChosen(tier) && (
                  <PiCheckBold size={12} color="var(--mantine-color-green-8)" />
                )}
              </Group>
            </Table.Th>
          ))}
          {isSingleTier && <Table.Th p={0} px={6} />}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => {
          // A service chosen on its own gets the same treatment the chosen package
          // column gets: green ground and a tick. It used to be marked only by its
          // name not being dimmed, which put a bought service and the two beside it
          // one shade apart and read, at a glance, as nothing having been chosen.
          const picked = isSingleTier && row.selected === true;
          const passedOver = isSingleTier && row.selected === false;
          return (
            <Table.Tr key={row.key}>
              <Table.Td
                p={0}
                px={6}
                style={{
                  minWidth: 0,
                  backgroundColor: picked ? CHOSEN_BG : undefined,
                }}
              >
                <Group gap={4} wrap="nowrap">
                  {picked && (
                    <PiCheckBold
                      size={12}
                      color="var(--mantine-color-green-8)"
                      style={{ flexShrink: 0 }}
                      aria-label="Chosen"
                    />
                  )}
                  <Text
                    fz="xs"
                    truncate
                    fw={picked ? 500 : undefined}
                    c={
                      picked
                        ? 'green.8'
                        : row.dimmed || passedOver
                          ? 'dimmed'
                          : undefined
                    }
                  >
                    {row.name}
                  </Text>
                </Group>
              </Table.Td>
              {isSingleTier ? (
                <Table.Td
                  p={0}
                  px={6}
                  ta="right"
                  style={{
                    whiteSpace: 'nowrap',
                    backgroundColor: picked ? CHOSEN_BG : undefined,
                  }}
                >
                  <Text fz="xs" c={picked ? 'green.8' : 'dimmed'} span>
                    {row.price}
                  </Text>
                </Table.Td>
              ) : (
                tierNames.map((_, tier) => (
                  <Table.Td
                    key={tier}
                    p={0}
                    px={6}
                    ta="center"
                    style={{
                      backgroundColor: isChosen(tier) ? CHOSEN_BG : undefined,
                    }}
                  >
                    <TierCellContent
                      cell={row.tiers?.[tier] ?? { included: false }}
                      dimmed={isDimmedTier(tier)}
                    />
                  </Table.Td>
                ))
              )}
            </Table.Tr>
          );
        })}
      </Table.Tbody>
      {totals && totals.length > 0 && (
        <Table.Tfoot>
          <Table.Tr>
            <Table.Td p={0} px={6}>
              <Text fz="xs" fw={600}>
                Total
              </Text>
            </Table.Td>
            {isSingleTier ? (
              <Table.Td
                p={0}
                px={6}
                ta="right"
                style={{ whiteSpace: 'nowrap' }}
              >
                <Text fz="xs" fw={600} span>
                  {totals[0]}
                </Text>
              </Table.Td>
            ) : (
              tierNames.map((_, tier) => (
                <Table.Td
                  key={tier}
                  p={0}
                  px={6}
                  ta="center"
                  style={{
                    whiteSpace: 'nowrap',
                    backgroundColor: isChosen(tier) ? CHOSEN_BG : undefined,
                  }}
                >
                  <Text
                    fz="xs"
                    fw={600}
                    span
                    c={
                      isChosen(tier)
                        ? 'green.8'
                        : isDimmedTier(tier)
                          ? 'dimmed'
                          : undefined
                    }
                  >
                    {totals[tier]}
                  </Text>
                </Table.Td>
              ))
            )}
          </Table.Tr>
        </Table.Tfoot>
      )}
    </Table>
  );
}
